/*
  This helper hook, helps determine correct placing & alignment of the dropdown based on the triggerRef position
  `requestAnimationFrame` is used to avoid glitch appearance of the dropdown at the very top of the page
  on first load.

  We need a scroll & resize listeners, to re-determine where the dropdown should be placed.
  If there is not enough space to render the dropdown on determined placement, it will be rendered on the opposite side
  of the trigger.

  There are options to ignore this behavior with the placementFallback prop, to force the dropdown
  to be rendered on the determined placement always.

  We can also set the alignment of the dropdown.

  Another option is to ignore the width of the trigger, and let the dropdown content determine the width.

  compensateScroll - browser handles positioning automatically (including scroll) UNLESS
  native UI changes occur (e.g., mobile keyboard opening on input focus),
  in which case we need to manually compensate for scroll offset
*/
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useState, useEffect } from 'react';
import { calculatePositionCoordinates, determineOptimalPlacement } from './utils';
var baseStyles = {
    position: 'fixed',
    // 1 point above DLS/modal to be above it
    zIndex: 101,
};
export var useDropdownPosition = function (isOpen, triggerElement, contentBounds, placement, alignment, placementFallback, matchTriggerWidth, compensateScroll) {
    if (placement === void 0) { placement = 'bottom'; }
    if (alignment === void 0) { alignment = 'start'; }
    if (placementFallback === void 0) { placementFallback = 'auto'; }
    if (matchTriggerWidth === void 0) { matchTriggerWidth = true; }
    if (compensateScroll === void 0) { compensateScroll = false; }
    var _a = useState(null), position = _a[0], setPosition = _a[1];
    var _b = useState(false), initialized = _b[0], setInitialized = _b[1];
    useEffect(function () {
        if (isOpen && triggerElement) {
            requestAnimationFrame(function () {
                setInitialized(true);
            });
        }
        return function () {
            setInitialized(false);
        };
    }, [isOpen, triggerElement]);
    useEffect(function () {
        if (!isOpen) {
            setPosition(null);
            return;
        }
        if (!triggerElement)
            return;
        var calculatePosition = function () {
            var rect = triggerElement.getBoundingClientRect();
            var contentHeight = (contentBounds === null || contentBounds === void 0 ? void 0 : contentBounds.height) || 0;
            var contentWidth = matchTriggerWidth ? rect.width : (contentBounds === null || contentBounds === void 0 ? void 0 : contentBounds.width) || 0;
            var _a = determineOptimalPlacement(placement, alignment, placementFallback, rect, contentHeight, contentWidth), finalPlacement = _a.finalPlacement, finalAlignment = _a.finalAlignment;
            // Calculate position
            var newPosition = calculatePositionCoordinates(rect, contentHeight, contentWidth, finalPlacement, finalAlignment, matchTriggerWidth, placementFallback, compensateScroll);
            setPosition(newPosition);
        };
        calculatePosition();
        window.addEventListener('scroll', calculatePosition, true);
        window.addEventListener('resize', calculatePosition);
        // eslint-disable-next-line consistent-return
        return function () {
            window.removeEventListener('scroll', calculatePosition, true);
            window.removeEventListener('resize', calculatePosition);
        };
    }, [
        isOpen,
        initialized,
        placement,
        alignment,
        placementFallback,
        matchTriggerWidth,
        triggerElement,
        contentBounds,
        compensateScroll,
    ]);
    var hasValidMeasurements = contentBounds && contentBounds.width > 0 && contentBounds.height > 0;
    // We basically "wait" until we're able to calculate the position, by rendering the content hidden for 1 frame
    if (!initialized || !position || !hasValidMeasurements) {
        return __assign(__assign({}, baseStyles), { opacity: 0 });
    }
    return __assign(__assign(__assign({}, baseStyles), position), { opacity: 1 });
};
